import { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Leaf, 
  Menu, 
  X, 
  ArrowUpRight, 
  LogOut, 
  Smartphone, 
  Play, 
  LogIn, 
  Star, 
  Sparkles,
  LayoutDashboard,
  History,
  Heart,
  Settings,
  User,
  ChevronDown,
  Shield,
  Bookmark,
  Wrench,
  Droplets,
  Filter,
  Sun,
  Moon,
  Spade,
  Activity,
  FileText,
  BookOpen,
  LifeBuoy,
  Search,
  Calendar,
  Clock,
  Tag,
  ChevronRight,
  Home,
  Database,
  Info,
  Trash2
} from "lucide-react";
import { UserState } from "../types";
import { db, isRealFirebaseEnabled } from "../lib/firebase";
import { collection, onSnapshot, doc, setDoc, getDocs, query, limit } from "firebase/firestore";
import { staticTools } from "../data/staticTools";
import { PUBLISHED_BLOGS_FALLBACK } from "../data/publishedBlogsFallback";
import { getBlogImage } from "../utils";

function getToolIcon(iconName: string) {
  switch (iconName) {
    case "Droplets":
    case "Droplet":
    case "watering":
      return Droplets;
    case "Filter":
    case "filter":
    case "ph":
      return Filter;
    case "Sun":
    case "sun":
    case "sunlight":
      return Sun;
    case "Spade":
    case "spade":
    case "fertilizer":
    case "shovel":
      return Spade;
    case "Activity":
    case "activity":
      return Activity;
    case "Sparkles":
    case "sparkles":
      return Sparkles;
    case "Leaf":
    case "leaf":
      return Leaf;
    case "Settings":
    case "settings":
      return Settings;
    case "FileText":
      return FileText;
    case "BookOpen":
      return BookOpen;
    default:
      return Wrench;
  }
}

function getToolIconColor(iconName: string) {
  switch (iconName) {
    case "Droplets":
    case "Droplet":
    case "watering":
      return { bg: "bg-blue-50 dark:bg-blue-950/50", text: "text-blue-600 dark:text-blue-400" };
    case "Filter":
    case "filter":
    case "ph":
      return { bg: "bg-purple-50 dark:bg-purple-950/50", text: "text-purple-600 dark:text-purple-400" };
    case "Sun":
    case "sun":
    case "sunlight":
      return { bg: "bg-amber-50 dark:bg-amber-950/50", text: "text-amber-500 dark:text-amber-450" };
    case "Spade":
    case "spade":
    case "fertilizer":
    case "shovel":
      return { bg: "bg-orange-50 dark:bg-orange-950/50", text: "text-orange-600 dark:text-orange-400" };
    case "Activity":
    case "activity":
      return { bg: "bg-rose-50 dark:bg-rose-950/50", text: "text-rose-600 dark:text-rose-400" };
    case "Sparkles":
    case "sparkles":
      return { bg: "bg-pink-50 dark:bg-pink-950/50", text: "text-pink-600 dark:text-pink-400" };
    case "Leaf":
    case "leaf":
      return { bg: "bg-emerald-50 dark:bg-emerald-950/50", text: "text-emerald-600 dark:text-emerald-450" };
    default:
      return { bg: "bg-emerald-50 dark:bg-emerald-950/50", text: "text-emerald-600 dark:text-emerald-450" };
  }
}

interface HeaderProps {
  onIdentifyClick: () => void;
  onGetAppClick: () => void;
  user: UserState | null;
  onSignInClick: () => void;
  onSignOut: () => void;
  onDashboardClick?: (tab?: any) => void;
  navigate?: (path: string) => void;
  tools?: any[];
  isAdmin?: boolean;
}

export default function Header({
  onIdentifyClick,
  onGetAppClick,
  user,
  onSignInClick,
  onSignOut,
  onDashboardClick,
  navigate,
  tools,
  isAdmin = false,
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const [isMobileToolsOpen, setIsMobileToolsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dbBlogs, setDbBlogs] = useState<any[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const toolsDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [activeTools, setActiveTools] = useState<any[]>(staticTools);
  const [actualIsAdmin, setActualIsAdmin] = useState(false);

  const [activeTab, setActiveTab] = useState("Home");

  const pillTabs = [
    { name: "Home", href: "#home", icon: Home, iconColor: "text-emerald-500" },
    { name: "Tools", dropdown: true, icon: Wrench, iconColor: "text-blue-500" },
    { name: "Plant Database", href: "#database", path: "/database", icon: Database, iconColor: "text-teal-500" },
    { name: "Blog", href: "#blog", path: "/blog", icon: BookOpen, iconColor: "text-amber-500" },
    { name: "About", href: "#about", path: "/about", icon: Info, iconColor: "text-purple-500" },
  ];

  // Monitor location path, hash changes and scroll sections to auto-set active tab
  useEffect(() => {
    const handleLocationChange = () => {
      const path = window.location.pathname;
      const hash = window.location.hash;

      if (path === "/database") {
        setActiveTab("Plant Database");
      } else if (path === "/blog") {
        setActiveTab("Blog");
      } else if (path === "/about") {
        setActiveTab("About");
      } else {
        if (hash === "#features") {
          setActiveTab("Features");
        } else if (hash === "#how-it-works") {
          setActiveTab("How It Works");
        } else if (hash === "#database") {
          setActiveTab("Plant Database");
        } else if (hash === "#blog") {
          setActiveTab("Blog");
        } else if (hash === "#about") {
          setActiveTab("About");
        } else {
          setActiveTab("Home");
        }
      }
    };

    handleLocationChange();
    window.addEventListener("hashchange", handleLocationChange);
    window.addEventListener("popstate", handleLocationChange);

    // Dynamic Section Scroll Highlighter on homepage
    let observer: IntersectionObserver | null = null;
    if (window.location.pathname === "/") {
      const sections = ["home", "features", "how-it-works", "database", "blog", "about"];
      const elements = sections.map(id => document.getElementById(id)).filter(Boolean);

      if (elements.length > 0) {
        observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const id = entry.target.id;
              if (id === "home") setActiveTab("Home");
              else if (id === "features") setActiveTab("Features");
              else if (id === "how-it-works") setActiveTab("How It Works");
              else if (id === "database") setActiveTab("Plant Database");
              else if (id === "blog") setActiveTab("Blog");
              else if (id === "about") setActiveTab("About");
            }
          });
        }, {
          rootMargin: "-25% 0px -55% 0px"
        });

        elements.forEach(el => observer?.observe(el!));
      }
    }

    return () => {
      window.removeEventListener("hashchange", handleLocationChange);
      window.removeEventListener("popstate", handleLocationChange);
      observer?.disconnect();
    };
  }, [navigate]);

  const [theme, setTheme] = useState<"light" | "dark" | null>(null);

  // Initialize theme on mount to prevent SSR mismatches
  useEffect(() => {
    const saved = localStorage.getItem("theme");
    if (saved === "dark") {
      setTheme("dark");
    } else if (saved === "light") {
      setTheme("light");
    } else {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      setTheme(prefersDark ? "dark" : "light");
    }
  }, []);

  // Sync theme with HTML element and localStorage
  useEffect(() => {
    if (theme === null) return;
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Keyboard shortcut Ctrl+K or Cmd+K to open search modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsSearchOpen(true);
      }
      if (e.key === "Escape") {
        setIsSearchOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Autofocus search input when open
  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      setSearchQuery("");
    }
  }, [isSearchOpen]);

  // Fetch blogs for search on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem("cached_blog_articles");
      if (cached && cached.includes('"status":')) {
        setDbBlogs(JSON.parse(cached));
      } else {
        localStorage.removeItem("cached_blog_articles");
        localStorage.removeItem("cached_blog_articles_timestamp");
      }
    } catch (e) {
      // Silently catch
    }

    async function loadBlogs() {
      if (db && isRealFirebaseEnabled) {
        try {
          const snap = await getDocs(query(collection(db, "blog_articles"), limit(300)));
          const list: any[] = [];
          snap.forEach(docNode => {
            list.push({ id: docNode.id, ...docNode.data() });
          });
          setDbBlogs(list);

          // Minify stored blog properties to save localStorage space (only keep search-essential fields)
          try {
            const minified = list.map(b => ({
              id: b.id,
              slug: b.slug || "",
              image: b.image || "",
              category: b.category || "",
              reading_time: b.reading_time || "",
              title: b.title || "",
              summary: b.summary || "",
              status: b.status || "published"
            }));
            localStorage.setItem("cached_blog_articles", JSON.stringify(minified));
          } catch (storageErr) {
            // Silently ignore storage quota/privacy limits
          }
        } catch (err) {
          // Log silently if needed, but do not trigger alert or warning banner
          console.log("Blogs loaded with warning suppression:", err);
        }
      }
    }
    loadBlogs();
  }, []);

  useEffect(() => {
    if (!user) {
      setActualIsAdmin(false);
      return;
    }
    if (user.email === "bishalkhanal7021@gmail.com") {
      if (user.uid.startsWith("simulated-") || user.uid.startsWith("mock-") || user.uid.startsWith("email-")) {
        const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        if (!isLocalhost) {
          setActualIsAdmin(false);
          return;
        }
      }
      setActualIsAdmin(true);
      return;
    }
    if (!isRealFirebaseEnabled || !db) {
      setActualIsAdmin(false);
      return;
    }
    const docRef = doc(db, "admin_roles", user.email);
    const unsubscribe = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.status === "active") {
          setActualIsAdmin(true);
        } else {
          setActualIsAdmin(false);
        }
      } else {
        setActualIsAdmin(false);
      }
    }, (error) => {
      console.warn("Could not listen to admin roles:", error);
      setActualIsAdmin(false);
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (tools && tools.length > 0) {
      const merged = [...tools];
      for (const sTool of staticTools) {
        if (!merged.some((m) => m.id === sTool.id)) {
          merged.push(sTool);
        }
      }
      setActiveTools(merged);
    } else {
      setActiveTools(staticTools);
    }
  }, [tools]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const clickedOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(e.target as Node);
      const clickedOutsideMobileDropdown = !mobileDropdownRef.current || !mobileDropdownRef.current.contains(e.target as Node);
      
      if (clickedOutsideDropdown && clickedOutsideMobileDropdown) {
        setIsProfileDropdownOpen(false);
      }
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(e.target as Node)) {
        setIsToolsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Monitor page scroll to apply background blur glassmorphism
  useEffect(() => {
    let ticking = false;
    const updateScroll = () => {
      setIsScrolled(window.scrollY > 15);
      ticking = false;
    };
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScroll);
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const publishedTools = activeTools.filter((t) => t.status !== "Draft");

  const identifiersList = publishedTools.filter((t) => {
    const term = ((t.name || "") + " " + (t.id || "") + " " + (t.description || "")).toLowerCase();
    return (
      term.includes("identifier") ||
      term.includes("identify") ||
      term.includes("scan") ||
      term.includes("flower") ||
      term.includes("tree")
    );
  });

  const calculatorsList = publishedTools.filter((t) => !identifiersList.some((idx) => idx.id === t.id));

  const combinedBlogs = useMemo(() => {
    const listMap = new Map<string, any>();

    // 1. Populate with 30 static articles
    PUBLISHED_BLOGS_FALLBACK.forEach(fb => {
      listMap.set(fb.slug, {
        id: fb.id,
        slug: fb.slug,
        title: fb.title,
        category: fb.category,
        reading_time: fb.reading_time,
        publish_date: fb.publish_date,
        summary: fb.summary,
        author: fb.author.name,
        image: getBlogImage(fb)
      });
    });

    // 2. Overlay db blogs ONLY if matching an existing static article
    if (dbBlogs && dbBlogs.length > 0) {
      dbBlogs.forEach(p => {
        const slug = p.slug || p.id;
        if (!slug) return;

        const existingKey = listMap.has(slug) ? slug : Array.from(listMap.keys()).find(k => k === p.id || k === p.slug);
        if (existingKey) {
          const status = (p.status || "published").toLowerCase();
          if (status === "published") {
            listMap.set(existingKey, {
              ...listMap.get(existingKey),
              title: p.title || listMap.get(existingKey).title,
              category: p.category || listMap.get(existingKey).category,
              reading_time: p.readTime || p.readingTime || p.reading_time || listMap.get(existingKey).reading_time,
              publish_date: p.date || p.publishDate || p.publish_date || listMap.get(existingKey).publish_date,
              summary: p.summary || p.snippet || p.excerpt || p.metaDescription || listMap.get(existingKey).summary,
              author: typeof p.author === 'object' ? (p.author.name || "Bishal Khanal") : (p.author || listMap.get(existingKey).author),
              image: getBlogImage(p) || listMap.get(existingKey).image
            });
          }
        }
      });
    }

    return Array.from(listMap.values());
  }, [dbBlogs]);

  // Filter search results in real time
  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return { tools: [], blogs: [] };
    }

    const matchedTools = publishedTools.filter(tool => {
      const name = (tool.name || "").toLowerCase();
      const desc = (tool.description || "").toLowerCase();
      const tags = (tool.tags || []).map((t: string) => t.toLowerCase());
      return name.includes(query) || desc.includes(query) || tags.some(t => t.includes(query));
    });

    const matchedBlogs = combinedBlogs.filter(blog => {
      const title = (blog.title || "").toLowerCase();
      const summary = (blog.summary || "").toLowerCase();
      const cat = (blog.category || "").toLowerCase();
      return title.includes(query) || summary.includes(query) || cat.includes(query);
    });

    return { tools: matchedTools, blogs: matchedBlogs };
  }, [searchQuery, publishedTools, combinedBlogs]);

  const menuItems = [
    { name: "Home", href: "#home" },
    { name: "Plant Database", href: "#database" },
    { name: "Blog", href: "#blog" },
    { name: "About", href: "#about" },
  ];

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleTabClick = (item: any) => {
    setActiveTab(item.name);
    
    if (item.dropdown) {
      setIsToolsDropdownOpen(!isToolsDropdownOpen);
      return;
    } else {
      setIsToolsDropdownOpen(false);
    }

    if (item.name === "Plant Database") {
      if (navigate) {
        navigate("/database");
      } else {
        handleNavClick("#database");
      }
    } else if (item.name === "About") {
      if (navigate) {
        navigate("/about");
      } else {
        handleNavClick("#about");
      }
    } else if (item.name === "Blog") {
      if (navigate) {
        navigate("/blog");
      } else {
        handleNavClick("#blog");
      }
    } else {
      if (window.location.pathname !== "/") {
        if (navigate) {
          navigate("/");
          setTimeout(() => {
            handleNavClick(item.href);
          }, 250);
        }
      } else {
        handleNavClick(item.href);
      }
    }
  };

  return (
    <>
      {/* Sticky Premium Header */}
      <header
        id="premium-header"
        className={`fixed top-0 left-0 right-0 h-20 z-50 transition-colors transition-shadow duration-300 ease-in-out print:hidden [transform:translateZ(0)] ${
          isScrolled
            ? "bg-white/90 dark:bg-slate-900/90 backdrop-blur-md shadow-sm border-b border-gray-100/80 dark:border-slate-800/80"
            : "bg-white dark:bg-slate-950 border-b border-gray-100/30 dark:border-slate-800/30"
        }`}
      >
        <div className="max-w-[1536px] mx-auto h-full px-4 sm:px-6 lg:px-6 xl:px-8 flex items-center justify-between">
          
          {/* Left Section: Logo & Branding */}
          <div 
            onClick={() => {
              if (window.location.pathname !== "/") {
                if (navigate) navigate("/");
              } else {
                handleNavClick("#home");
              }
            }}
            className="flex items-center gap-2 cursor-pointer group shrink-0"
          >
            <div className="relative">
              {/* Soft green glow background */}
              <div className="absolute -inset-1 bg-green-500/10 rounded-full blur-md opacity-70 group-hover:opacity-100 transition-opacity" />
              <div className="relative w-8.5 h-8.5 xl:w-10 xl:h-10 bg-gradient-to-tr from-green-500 to-emerald-400 rounded-xl flex items-center justify-center text-white shadow-sm shadow-green-200">
                <Leaf className="w-4.5 h-4.5 xl:w-5 xl:h-5 transition-transform group-hover:rotate-12 duration-300" />
              </div>
            </div>
            <div className="flex flex-col text-left">
              <span className="text-xs xs:text-sm sm:text-base xl:text-lg font-bold tracking-tight text-gray-900 flex items-center gap-1">
                Plant <span className="text-green-800 dark:text-green-400 font-extrabold">Identifier</span>
              </span>
              <span className="text-[8px] xs:text-[9px] xl:text-[10px] text-gray-600 dark:text-gray-300 font-semibold tracking-wider uppercase -mt-0.5 hidden xs:inline-block">
                AI BOTANIST
              </span>
            </div>
          </div>

          {/* Center Section: Navigation Links (Desktop/Tablet/Mobile Segmented Tab Control) */}
          <nav className="hidden xl:flex items-center shrink-0 overflow-visible mx-0.5 xs:mx-2 md:mx-4">
            <div className="bg-[#F5F5F5] dark:bg-slate-100/60 border border-gray-200/50 dark:border-slate-200/30 rounded-full p-0.5 sm:p-1 flex items-center gap-0.5 xs:gap-1.5 shadow-xs overflow-visible w-full">
              {pillTabs.map((item) => {
                const isActive = activeTab === item.name;
                return (
                  <div
                    key={item.name}
                    className="relative shrink-0 overflow-visible"
                    ref={item.dropdown ? toolsDropdownRef : undefined}
                    onMouseEnter={() => {
                      if (item.dropdown) setIsToolsDropdownOpen(true);
                    }}
                    onMouseLeave={() => {
                      if (item.dropdown) setIsToolsDropdownOpen(false);
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleTabClick(item);
                      }}
                      className={`relative flex items-center gap-1 sm:gap-2 px-1.5 xs:px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 rounded-full text-[10px] xs:text-[11px] md:text-xs font-black capitalize tracking-wider transition-all duration-200 select-none cursor-pointer whitespace-nowrap ${
                        isActive
                          ? "bg-white dark:bg-slate-50 text-gray-900 dark:text-white shadow-sm border border-gray-150/15 dark:border-slate-200/10"
                          : "text-gray-700 hover:text-gray-900 dark:text-slate-200 dark:hover:text-white hover:bg-gray-200/55 dark:hover:bg-slate-50/40"
                      }`}
                    >
                      {/* Unique Icon rendered dynamically */}
                      {(() => {
                        const IconComponent = item.icon;
                        return (
                          <IconComponent 
                            className={`w-3.5 h-3.5 shrink-0 transition-colors duration-200 ${
                              isActive ? "text-emerald-500 dark:text-emerald-400 animate-pulse" : item.iconColor
                            }`} 
                          />
                        );
                      })()}
                      
                      <span>
                        {item.name === "Plant Database" ? (
                          <>
                            <span className="hidden xs:inline">Plant </span>Database
                          </>
                        ) : (
                          item.name
                        )}
                      </span>
                      
                      {item.dropdown && (
                        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-300 ${isToolsDropdownOpen ? "rotate-180 text-emerald-500" : ""}`} />
                      )}
                    </button>

                    {item.dropdown && (
                      <AnimatePresence>
                        {isToolsDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{ duration: 0.15 }}
                            className="fixed md:absolute top-20 md:top-full left-2 right-2 xs:left-4 xs:right-4 md:left-[-220px] lg:left-1/2 lg:-translate-x-1/2 md:right-auto mt-3 w-auto md:w-[680px] bg-white dark:bg-[#0f172a] border border-gray-100 dark:border-slate-100/40 rounded-[28px] shadow-2xl p-5 md:p-6 z-55 flex flex-col gap-4 max-h-[calc(100vh-100px)] md:max-h-[90vh] overflow-y-auto"
                          >
                            {/* Professional Dropdown Header Callout */}
                            <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-100/40 pb-3.5 mb-1 select-none text-left">
                              <div className="space-y-1.5">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-green-50 dark:bg-green-950/40 text-[9px] font-black uppercase text-green-700 dark:text-green-400 tracking-wider border border-green-150 dark:border-green-900/40">
                                  <Sparkles className="w-2.5 h-2.5 text-green-600 dark:text-green-400 fill-green-600 dark:fill-green-400 animate-pulse" />
                                  Professional Hub
                                </span>
                                <h4 className="text-sm font-black text-gray-900 dark:text-white leading-tight">
                                  Everything You Need To Identify Plants Smarter
                                </h4>
                                <p className="text-[10px] text-gray-550 dark:text-gray-400 font-semibold leading-normal">
                                  Instant botanical solvers, diagnostics & calibration instruments
                                </p>
                              </div>
                              <span className="hidden md:inline-block text-[9px] font-bold text-gray-400 dark:text-gray-400 bg-gray-50 dark:bg-slate-800/50 border border-gray-100 dark:border-slate-100/40 px-2 py-1 rounded-lg">
                                100% Free AI Solvers
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Left Column: Botanical Identifiers */}
                              {identifiersList.length > 0 && (
                                <div className="flex flex-col gap-2">
                                  <div className="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl border border-emerald-100/50 dark:border-emerald-900/30 flex items-center gap-1.5 mb-1.5">
                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                                    <span className="text-[10px] font-black text-emerald-800 dark:text-emerald-400 uppercase tracking-widest block text-left">
                                      Botanical Identifiers
                                    </span>
                                  </div>
                                  
                                  <div className="flex flex-col gap-1">
                                    {identifiersList.map((tool) => {
                                      const ToolIcon = getToolIcon(tool.icon);
                                      const colors = getToolIconColor(tool.icon);
                                      return (
                                        <a
                                          key={tool.id}
                                          href={tool.link}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setIsToolsDropdownOpen(false);
                                            if (tool.link.startsWith("/")) {
                                              if (navigate) navigate(tool.link);
                                            } else {
                                              window.location.href = tool.link;
                                            }
                                          }}
                                          className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-emerald-50/30 dark:hover:bg-emerald-950/20 border border-transparent hover:border-emerald-100/20 dark:hover:border-emerald-900/20 transition-all group text-left cursor-pointer"
                                        >
                                          <div className={`p-2 rounded-xl ${colors.bg} ${colors.text} group-hover:scale-105 transition-all shrink-0 shadow-xs`}>
                                            <ToolIcon className="w-4 h-4" />
                                          </div>
                                          <div className="min-w-0">
                                            <span className="block text-xs font-bold text-gray-900 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-all">
                                              {tool.name}
                                            </span>
                                            <span className="block text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 leading-normal font-medium">
                                              {tool.description}
                                            </span>
                                          </div>
                                        </a>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Right Column: Gardening Calculators */}
                              {calculatorsList.length > 0 && (
                                <div className="flex flex-col gap-2 border-t md:border-t-0 md:border-l border-gray-100 dark:border-slate-100/40 pt-4 md:pt-0 md:pl-5">
                                  <div className="px-3 py-1.5 bg-green-50 dark:bg-green-950/30 rounded-xl border border-green-100/50 dark:border-green-900/30 flex items-center gap-1.5 mb-1.5">
                                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                                    <span className="text-[10px] font-black text-green-800 dark:text-green-400 uppercase tracking-widest block text-left">
                                      Gardening Calculators
                                    </span>
                                  </div>

                                  <div className="flex flex-col gap-1">
                                    {calculatorsList.map((tool) => {
                                      const ToolIcon = getToolIcon(tool.icon);
                                      const colors = getToolIconColor(tool.icon);
                                      return (
                                        <a
                                          key={tool.id}
                                          href={tool.link}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setIsToolsDropdownOpen(false);
                                            if (tool.link.startsWith("/")) {
                                              if (navigate) navigate(tool.link);
                                            } else {
                                              window.location.href = tool.link;
                                            }
                                          }}
                                          className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-green-50/30 dark:hover:bg-green-950/20 border border-transparent hover:border-green-100/20 dark:hover:border-green-900/20 transition-all group text-left cursor-pointer"
                                        >
                                          <div className={`p-2 rounded-xl ${colors.bg} ${colors.text} group-hover:scale-105 transition-all shrink-0 shadow-xs`}>
                                            <ToolIcon className="w-4 h-4" />
                                          </div>
                                          <div className="min-w-0">
                                            <span className="block text-xs font-bold text-gray-900 dark:text-white group-hover:text-green-700 dark:group-hover:text-green-400 transition-all">
                                              {tool.name}
                                            </span>
                                            <span className="block text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 leading-normal font-medium">
                                              {tool.description}
                                            </span>
                                          </div>
                                        </a>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          {/* Right Section: Actions Buttons */}
          <div className="hidden xl:flex items-center gap-1.5 xl:gap-3 shrink-0">
            {/* Search Trigger Button */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="flex items-center gap-1.5 xl:gap-2 px-2.5 xl:px-3.5 py-1.5 rounded-xl border border-gray-200/60 bg-gray-50/50 text-gray-400 hover:bg-gray-50 hover:text-gray-600 hover:border-gray-300 transition-all text-xs cursor-pointer group mr-0.5 shrink-0"
              title="Search (⌘K)"
            >
              <Search className="w-3.5 h-3.5 text-gray-400 group-hover:text-green-600 transition-colors shrink-0" />
              <span className="font-bold text-gray-500 hidden xl:inline">Search tools, blogs...</span>
              <span className="font-bold text-gray-500 xl:hidden">Search...</span>
              <kbd className="hidden xl:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9.5px] font-bold font-mono text-gray-700 bg-gray-100 border border-gray-300 rounded shadow-2xs select-none shrink-0">
                ⌘K
              </kbd>
            </button>

            {/* Premium Theme Toggle Button */}
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-2 bg-gray-50/50 hover:bg-gray-100/80 border border-gray-200/60 hover:border-gray-300 rounded-xl text-gray-500 hover:text-green-600 transition-all cursor-pointer flex items-center justify-center shrink-0"
              title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {theme === "dark" ? (
                <Sun className="w-4 h-4 text-amber-500 animate-[spin_4s_linear_infinite]" />
              ) : (
                <Moon className="w-4 h-4 text-indigo-500" />
              )}
            </button>

            {/* Sign In Button with premium floating dropdown menu */}
            {user ? (
              <div className="relative shrink-0" ref={dropdownRef}>
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200/50 rounded-xl transition-all cursor-pointer relative shrink-0"
                >
                  {user.photoURL ? (
                    <img 
                      src={user.photoURL} 
                      alt={user.name} 
                      className="w-6.5 h-6.5 rounded-full object-cover border border-green-500/10 shrink-0" 
                      referrerPolicy="no-referrer" 
                    />
                  ) : (
                    <div className="w-6.5 h-6.5 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-black text-xs uppercase border border-green-200 shrink-0">
                      {user.name.charAt(0)}
                    </div>
                  )}
                  <span className="text-xs font-bold text-gray-800 max-w-[70px] xl:max-w-[85px] truncate shrink-0">
                    {user.name.split(" ")[0]}
                  </span>
                  <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform duration-300 shrink-0 ${isProfileDropdownOpen ? "rotate-180" : ""}`} />
                </button>

                <AnimatePresence>
                  {isProfileDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 12, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 12, scale: 0.95 }}
                      transition={{ type: "spring", duration: 0.25, bounce: 0.1 }}
                      className="absolute right-0 mt-3.5 w-76 bg-white dark:bg-slate-50 border border-gray-150 rounded-2xl shadow-2xl py-3 z-50 overflow-hidden"
                    >
                      {/* Dropdown Header Block */}
                      <div className="px-4 pb-3 pt-1 border-b border-gray-100 flex items-center gap-3">
                        {user.photoURL ? (
                          <img 
                            src={user.photoURL} 
                            alt={user.name} 
                            className="w-10 h-10 rounded-full object-cover border border-green-500/10 shrink-0" 
                            referrerPolicy="no-referrer" 
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-black text-sm uppercase border border-green-200 shrink-0">
                            {user.name.charAt(0)}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <span className="text-xs font-black text-gray-900 block truncate leading-none mb-1">
                            {user.name}
                          </span>
                          <span className="text-[10px] text-gray-400 block truncate font-medium leading-none mb-1">
                            {user.email}
                          </span>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-green-50 text-[8px] font-black text-green-700 uppercase tracking-wider border border-green-100/55 scale-95 origin-left">
                            Free Member
                          </span>
                        </div>
                      </div>

                      {/* Scrollable container for groups if screen is short, though 76 width fits perfectly */}
                      <div className="max-h-[70vh] overflow-y-auto custom-scrollbar p-1.5 space-y-3">
                        
                        {/* SECTION 1: WORKSPACE */}
                        <div className="space-y-0.5">
                          <span className="px-3 py-1 block text-[9px] font-black uppercase tracking-widest text-gray-400">
                            Botanist Workspace
                          </span>
                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("overview");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-green-700 hover:bg-green-50/50 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-green-50 text-green-600 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <LayoutDashboard className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-green-700">Dashboard Home</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Instant care stats & diagnostic center</span>
                            </div>
                          </button>

                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("collection");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-emerald-700 hover:bg-emerald-50/40 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Leaf className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-emerald-700">Plant Collection</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Virtual greenhouse pot garden</span>
                            </div>
                          </button>
                        </div>

                        {/* SECTION 2: ARCHIVES */}
                        <div className="space-y-0.5 border-t border-gray-50 pt-2">
                          <span className="px-3 py-1 block text-[9px] font-black uppercase tracking-widest text-gray-400">
                            My Garden Archives
                          </span>
                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("history");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-blue-700 hover:bg-blue-50/40 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-blue-50 text-blue-500 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <History className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-blue-700">Scan History</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Historical identification assessments</span>
                            </div>
                          </button>

                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("favorites");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-rose-700 hover:bg-rose-50/30 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-rose-50 text-rose-500 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Heart className="w-3.5 h-3.5 fill-rose-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-rose-700">Starred Favorites</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Fast-access marked botanical species</span>
                            </div>
                          </button>

                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("vault");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-amber-700 hover:bg-amber-50/30 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-amber-50 text-amber-600 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Bookmark className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-amber-700">My Plant Vault</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Saved scientific guides & articles</span>
                            </div>
                          </button>
                        </div>

                        {/* SECTION 3: REVIEWS & SUPPORT */}
                        <div className="space-y-0.5 border-t border-gray-50 pt-2">
                          <span className="px-3 py-1 block text-[9px] font-black uppercase tracking-widest text-gray-400">
                            Activity & Feedback
                          </span>
                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("reviews");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-amber-700 hover:bg-amber-50/20 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-amber-50 text-amber-500 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-amber-700">My Reviews</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Ratings & expert reviews published</span>
                            </div>
                          </button>

                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              if (navigate) {
                                navigate("/contact");
                              } else {
                                window.history.pushState(null, "", "/contact");
                                window.dispatchEvent(new PopStateEvent("popstate"));
                              }
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-indigo-700 hover:bg-indigo-50/30 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-indigo-50 text-indigo-500 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <LifeBuoy className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-indigo-700">Support Center</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Submit help tickets & documentation</span>
                            </div>
                          </button>
                        </div>

                        {/* SECTION 4: SETTINGS & CONSOLE */}
                        <div className="space-y-0.5 border-t border-gray-50 pt-2">
                          <span className="px-3 py-1 block text-[9px] font-black uppercase tracking-widest text-gray-400">
                            Account Configuration
                          </span>
                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("settings");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-slate-900 hover:bg-slate-100/40 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-slate-100 text-slate-600 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Settings className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-slate-900">Account Settings</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Configure passwords & details</span>
                            </div>
                          </button>

                          {actualIsAdmin && (
                            <button
                              onClick={() => {
                                setIsProfileDropdownOpen(false);
                                if (navigate) {
                                  navigate("/admin");
                                } else {
                                  window.history.pushState(null, "", "/admin");
                                  window.dispatchEvent(new PopStateEvent("popstate"));
                                }
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50/40 transition-colors cursor-pointer text-left group"
                            >
                              <div className="p-1.5 bg-emerald-50 text-emerald-650 rounded-lg group-hover:scale-105 transition-all shrink-0">
                                <Shield className="w-3.5 h-3.5" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className="block font-extrabold text-emerald-700">Admin Console</span>
                                <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Control panels & metrics dashboard</span>
                              </div>
                            </button>
                          )}

                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("delete-account");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-red-700 hover:bg-red-50/20 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-red-50 text-red-500 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Trash2 className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-red-700">Delete Account</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Irreversibly purge files & account</span>
                            </div>
                          </button>
                        </div>

                      </div>

                      {/* Dropdown Logout button */}
                      <div className="p-1.5 border-t border-gray-100 bg-gray-50/80 mt-1">
                        <button
                          onClick={() => {
                            setIsProfileDropdownOpen(false);
                            onSignOut();
                          }}
                          className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors cursor-pointer text-left group"
                        >
                          <div className="p-1.5 bg-red-50 text-red-600 rounded-lg group-hover:scale-110 transition-transform shrink-0">
                            <LogOut className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <span className="block font-black text-red-600">Logout Account</span>
                            <span className="block text-[9px] text-red-400 font-medium">Terminate current session safely</span>
                          </div>
                        </button>
                      </div>

                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <button
                id="signin-btn"
                onClick={onSignInClick}
                className="px-2.5 xl:px-4 py-2 text-xs xl:text-[13px] font-bold text-gray-800 bg-gray-100/30 border border-gray-200/50 rounded-xl hover:bg-gray-100/60 transition-all duration-200 flex items-center gap-1.5 cursor-pointer shrink-0"
              >
                <LogIn className="w-4 h-4 text-gray-500 shrink-0" />
                Sign In
              </button>
            )}

            {/* 3. Primary Gradient CTA Button */}
            <button
              id="identify-free-btn"
              onClick={onIdentifyClick}
              className="px-2.5 xl:px-4.5 py-2 xl:py-2.5 text-xs xl:text-[13px] font-extrabold text-white bg-gradient-to-r from-green-600 to-emerald-500 rounded-xl shadow-xs shadow-green-100 hover:shadow-lg hover:shadow-green-100 hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-1.5 cursor-pointer shrink-0"
            >
              <Sparkles className="w-4 h-4 shrink-0" />
              <span>Identify Plant Free</span>
            </button>
          </div>

          {/* Hamburger Menu Icon (Mobile/Tablet) */}
          <div className="flex xl:hidden items-center gap-2">
            {/* Mobile Search Button */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 sm:p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl transition-all cursor-pointer"
              aria-label="Search"
            >
              <Search className="w-5 h-5 text-gray-550" />
            </button>

            {/* Mobile Theme Toggle Button */}
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-2 sm:p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl transition-all cursor-pointer flex items-center justify-center"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? (
                <Sun className="w-5 h-5 text-amber-500 animate-[spin_4s_linear_infinite]" />
              ) : (
                <Moon className="w-5 h-5 text-indigo-500" />
              )}
            </button>

            {user && (
              <div className="relative shrink-0" ref={mobileDropdownRef}>
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center justify-center p-0.5 rounded-full bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 border border-gray-200/50 transition-all cursor-pointer relative shrink-0"
                >
                  {user.photoURL ? (
                    <img 
                      src={user.photoURL} 
                      alt={user.name} 
                      className="w-8 h-8 rounded-full object-cover border border-green-500/10 shrink-0" 
                      referrerPolicy="no-referrer" 
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold text-xs uppercase border border-green-400/50 shrink-0">
                      {user.name.charAt(0)}
                    </div>
                  )}
                </button>

                <AnimatePresence>
                  {isProfileDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 12, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 12, scale: 0.95 }}
                      transition={{ type: "spring", duration: 0.25, bounce: 0.1 }}
                      className="absolute right-[-12px] sm:right-0 mt-3.5 w-72 sm:w-76 bg-white dark:bg-slate-50 border border-gray-150 rounded-2xl shadow-2xl py-3 z-50 overflow-hidden"
                    >
                      {/* Dropdown Header Block */}
                      <div className="px-4 pb-3 pt-1 border-b border-gray-100 flex items-center gap-3">
                        {user.photoURL ? (
                          <img 
                            src={user.photoURL} 
                            alt={user.name} 
                            className="w-10 h-10 rounded-full object-cover border border-green-500/10 shrink-0" 
                            referrerPolicy="no-referrer" 
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-black text-sm uppercase border border-green-200 shrink-0">
                            {user.name.charAt(0)}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <span className="text-xs font-black text-gray-900 block truncate leading-none mb-1">
                            {user.name}
                          </span>
                          <span className="text-[10px] text-gray-400 block truncate font-medium leading-none mb-1">
                            {user.email}
                          </span>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-green-50 text-[8px] font-black text-green-700 uppercase tracking-wider border border-green-100/55 scale-95 origin-left">
                            Free Member
                          </span>
                        </div>
                      </div>

                      {/* Scrollable container for groups */}
                      <div className="max-h-[60vh] overflow-y-auto custom-scrollbar p-1.5 space-y-3">
                        
                        {/* SECTION 1: WORKSPACE */}
                        <div className="space-y-0.5">
                          <span className="px-3 py-1 block text-[9px] font-black uppercase tracking-widest text-gray-400">
                            Botanist Workspace
                          </span>
                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("overview");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-green-700 hover:bg-green-50/50 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-green-50 text-green-600 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <LayoutDashboard className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-green-700">Dashboard Home</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Instant care stats & diagnostic center</span>
                            </div>
                          </button>

                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("collection");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-emerald-700 hover:bg-emerald-50/40 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Leaf className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-emerald-700">Plant Collection</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Virtual greenhouse pot garden</span>
                            </div>
                          </button>
                        </div>

                        {/* SECTION 2: ARCHIVES */}
                        <div className="space-y-0.5 border-t border-gray-50 pt-2">
                          <span className="px-3 py-1 block text-[9px] font-black uppercase tracking-widest text-gray-400">
                            My Garden Archives
                          </span>
                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("history");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-blue-700 hover:bg-blue-50/40 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-blue-50 text-blue-500 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <History className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-blue-700">Scan History</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Historical identification assessments</span>
                            </div>
                          </button>

                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("favorites");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-rose-700 hover:bg-rose-50/30 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-rose-50 text-rose-500 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Heart className="w-3.5 h-3.5 fill-rose-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-rose-700">Starred Favorites</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Fast-access marked botanical species</span>
                            </div>
                          </button>

                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("vault");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-amber-700 hover:bg-amber-50/30 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-amber-50 text-amber-600 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Bookmark className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-amber-700">My Plant Vault</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Saved scientific guides & articles</span>
                            </div>
                          </button>
                        </div>

                        {/* SECTION 3: REVIEWS & SUPPORT */}
                        <div className="space-y-0.5 border-t border-gray-50 pt-2">
                          <span className="px-3 py-1 block text-[9px] font-black uppercase tracking-widest text-gray-400">
                            Activity & Feedback
                          </span>
                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("reviews");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-amber-700 hover:bg-amber-50/20 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-amber-50 text-amber-500 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-amber-700">My Reviews</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Ratings & expert reviews published</span>
                            </div>
                          </button>

                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              if (navigate) {
                                navigate("/contact");
                              } else {
                                window.history.pushState(null, "", "/contact");
                                window.dispatchEvent(new PopStateEvent("popstate"));
                              }
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-indigo-700 hover:bg-indigo-50/30 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-indigo-50 text-indigo-500 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <LifeBuoy className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-indigo-700">Support Center</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Submit help tickets & documentation</span>
                            </div>
                          </button>
                        </div>

                        {/* SECTION 4: SETTINGS & CONSOLE */}
                        <div className="space-y-0.5 border-t border-gray-50 pt-2">
                          <span className="px-3 py-1 block text-[9px] font-black uppercase tracking-widest text-gray-400">
                            Account Configuration
                          </span>
                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("settings");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-slate-900 hover:bg-slate-100/40 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-slate-100 text-slate-600 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Settings className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-slate-900">Account Settings</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Configure passwords & details</span>
                            </div>
                          </button>

                          {actualIsAdmin && (
                            <button
                              onClick={() => {
                                setIsProfileDropdownOpen(false);
                                if (navigate) {
                                  navigate("/admin");
                                } else {
                                  window.history.pushState(null, "", "/admin");
                                  window.dispatchEvent(new PopStateEvent("popstate"));
                                }
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50/40 transition-colors cursor-pointer text-left group"
                            >
                              <div className="p-1.5 bg-emerald-50 text-emerald-650 rounded-lg group-hover:scale-105 transition-all shrink-0">
                                <Shield className="w-3.5 h-3.5" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className="block font-extrabold text-emerald-700">Admin Console</span>
                                <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Control panels & metrics dashboard</span>
                              </div>
                            </button>
                          )}

                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              onDashboardClick?.("delete-account");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold text-gray-600 hover:text-red-700 hover:bg-red-50/20 transition-colors cursor-pointer text-left group"
                          >
                            <div className="p-1.5 bg-red-50 text-red-500 rounded-lg group-hover:scale-105 transition-all shrink-0">
                              <Trash2 className="w-3.5 h-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block font-extrabold text-gray-800 group-hover:text-red-700">Delete Account</span>
                              <span className="block text-[9px] text-gray-400 font-medium truncate mt-0.5">Irreversibly purge files & account</span>
                            </div>
                          </button>
                        </div>

                      </div>

                      {/* Dropdown Logout button */}
                      <div className="p-1.5 border-t border-gray-100 bg-gray-50/80 mt-1">
                        <button
                          onClick={() => {
                            setIsProfileDropdownOpen(false);
                            onSignOut();
                          }}
                          className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors cursor-pointer text-left group"
                        >
                          <div className="p-1.5 bg-red-50 text-red-600 rounded-lg group-hover:scale-110 transition-transform shrink-0">
                            <LogOut className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <span className="block font-black text-red-600">Logout Account</span>
                            <span className="block text-[9px] text-red-400 font-medium">Terminate current session safely</span>
                          </div>
                        </button>
                      </div>

                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 sm:p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl transition-all cursor-pointer"
              aria-label="Toggle Navigation menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

        </div>
      </header>

      {/* Mobile Drawer Navigation overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ type: "spring", duration: 0.15 }}
            className="fixed top-20 left-0 right-0 max-h-[calc(100vh-80px)] bg-white dark:bg-slate-50 z-40 border-b border-gray-100 dark:border-slate-100/40 shadow-xl overflow-y-auto flex flex-col p-6 xl:hidden"
          >
            <div className="space-y-4">
              <span className="text-[11px] font-bold text-gray-650 tracking-wider uppercase block">
                NAVIGATION
              </span>
              <div className="grid grid-cols-2 gap-2">
                {pillTabs.filter(item => !item.dropdown).map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <button
                      key={item.name}
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        handleTabClick(item);
                      }}
                      className="flex items-center gap-2.5 text-left py-3 px-4 rounded-xl text-sm font-semibold text-gray-700 hover:text-green-600 hover:bg-green-50/50 transition-all cursor-pointer"
                    >
                      <IconComponent className={`w-4 h-4 ${item.iconColor}`} />
                      <span>{item.name}</span>
                    </button>
                  );
                })}
              </div>

              {/* Collapsible Mobile Tools Panel */}
              <div className="border-t border-gray-100/60 pt-3">
                <button
                  onClick={() => setIsMobileToolsOpen(!isMobileToolsOpen)}
                  className="w-full flex items-center justify-between py-2 px-3 rounded-xl text-xs font-bold text-gray-800 hover:bg-gray-50 transition-all text-left"
                >
                  <span className="flex items-center gap-2">
                    <Wrench className="w-3.5 h-3.5 text-green-600" />
                    Tools & Calculators
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${isMobileToolsOpen ? "rotate-180" : ""}`} />
                </button>

                <AnimatePresence>
                  {isMobileToolsOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden mt-1 pl-3 pr-1 space-y-3"
                    >
                      {/* Mobile Identifiers */}
                      {identifiersList.length > 0 && (
                        <div className="space-y-1">
                          <span className="block text-[9px] font-black tracking-widest uppercase text-emerald-800 dark:text-emerald-400 ml-1 mt-1 text-left">
                            Botanical Identifiers
                          </span>
                          {identifiersList.map((tool) => {
                            const ToolIcon = getToolIcon(tool.icon);
                            const colors = getToolIconColor(tool.icon);
                            return (
                              <button
                                key={tool.id}
                                onClick={() => {
                                  setIsMobileMenuOpen(false);
                                  setIsMobileToolsOpen(false);
                                  if (tool.link.startsWith("/")) {
                                    if (navigate) navigate(tool.link);
                                  } else {
                                    window.location.href = tool.link;
                                  }
                                }}
                                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors text-left cursor-pointer"
                              >
                                <div className={`p-1 px-1.5 rounded-lg ${colors.bg} ${colors.text}`}>
                                  <ToolIcon className="w-3.5 h-3.5" />
                                </div>
                                <div className="min-w-0">
                                  <span className="block text-xs font-bold text-gray-800">{tool.name}</span>
                                  <span className="block text-[10px] text-gray-400 leading-tight mt-0.5">{tool.description}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Mobile Calculators */}
                      {calculatorsList.length > 0 && (
                        <div className="space-y-1">
                          <span className="block text-[9px] font-black tracking-widest uppercase text-green-800 dark:text-green-400 ml-1 text-left">
                            Gardening Calculators
                          </span>
                          {calculatorsList.map((tool) => {
                            const ToolIcon = getToolIcon(tool.icon);
                            const colors = getToolIconColor(tool.icon);
                            return (
                              <button
                                key={tool.id}
                                onClick={() => {
                                  setIsMobileMenuOpen(false);
                                  setIsMobileToolsOpen(false);
                                  if (tool.link.startsWith("/")) {
                                    if (navigate) navigate(tool.link);
                                  } else {
                                    window.location.href = tool.link;
                                  }
                                }}
                                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors text-left cursor-pointer"
                              >
                                <div className={`p-1 px-1.5 rounded-lg ${colors.bg} ${colors.text}`}>
                                  <ToolIcon className="w-3.5 h-3.5" />
                                </div>
                                <div className="min-w-0">
                                  <span className="block text-xs font-bold text-gray-800">{tool.name}</span>
                                  <span className="block text-[10px] text-gray-400 leading-tight mt-0.5">{tool.description}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="border-t border-gray-100 my-6 pt-6 space-y-4">
              <span className="text-[11px] font-bold text-gray-650 tracking-wider uppercase block">
                ACTIONS
              </span>
              <div className="flex flex-col gap-3">
                {/* Identify Free Link */}
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    onIdentifyClick();
                  }}
                  className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white rounded-xl font-bold text-sm tracking-wide shadow-sm flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Sparkles className="w-4 h-4" />
                  Identify Plant Free
                </button>

                {/* Simulated authentication sign in */}
                {user ? (
                  <div className="p-4 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-100 flex flex-col gap-3.5 text-left">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {user.photoURL ? (
                          <img 
                            src={user.photoURL} 
                            alt={user.name} 
                            className="w-10 h-10 rounded-full object-cover border border-green-500/10 shrink-0" 
                            referrerPolicy="no-referrer" 
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-black text-sm uppercase border border-green-200 shrink-0">
                            {user.name.charAt(0)}
                          </div>
                        )}
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-black text-gray-800 dark:text-stone-100 truncate max-w-[140px] leading-tight">{user.name}</span>
                          <span className="text-[10px] text-gray-500 dark:text-stone-400 truncate max-w-[140px] mt-0.5">{user.email}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          onSignOut();
                        }}
                        className="p-2 text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200/55 cursor-pointer shadow-3xs"
                        title="Sign Out"
                      >
                        <LogOut className="w-4 h-4" />
                      </button>
                    </div>

                    {/* All Dashboard subsections listed beautifully */}
                    <div className="border-t border-gray-200/40 pt-3.5 space-y-1.5">
                      <span className="px-1 block text-[8px] font-black uppercase tracking-widest text-gray-650 mb-1">
                        Workspace Links
                      </span>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            onDashboardClick?.("overview");
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200/50 text-xs font-bold text-gray-700 hover:text-green-700 hover:bg-green-50/50 cursor-pointer text-left"
                        >
                          <LayoutDashboard className="w-3.5 h-3.5 text-green-600 shrink-0" />
                          <span>Dashboard Home</span>
                        </button>

                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            onDashboardClick?.("collection");
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200/50 text-xs font-bold text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50 cursor-pointer text-left"
                        >
                          <Leaf className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          <span>Plant Collection</span>
                        </button>

                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            onDashboardClick?.("history");
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200/50 text-xs font-bold text-gray-700 hover:text-blue-700 hover:bg-blue-50/50 cursor-pointer text-left"
                        >
                          <History className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          <span>Scan History</span>
                        </button>

                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            onDashboardClick?.("favorites");
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200/50 text-xs font-bold text-gray-700 hover:text-rose-700 hover:bg-rose-50/50 cursor-pointer text-left"
                        >
                          <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-50 shrink-0" />
                          <span>Starred Favorites</span>
                        </button>

                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            onDashboardClick?.("vault");
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200/50 text-xs font-bold text-gray-700 hover:text-amber-700 hover:bg-amber-50/50 cursor-pointer text-left"
                        >
                          <Bookmark className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          <span>My Plant Vault</span>
                        </button>

                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            onDashboardClick?.("reviews");
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200/50 text-xs font-bold text-gray-700 hover:text-amber-700 hover:bg-amber-50/50 cursor-pointer text-left"
                        >
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-50 shrink-0" />
                          <span>My Reviews</span>
                        </button>

                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            onDashboardClick?.("settings");
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200/50 text-xs font-bold text-gray-700 hover:text-slate-900 hover:bg-slate-100/40 cursor-pointer text-left"
                        >
                          <Settings className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                          <span>Account Settings</span>
                        </button>

                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            if (navigate) {
                              navigate("/contact");
                            } else {
                              window.history.pushState(null, "", "/contact");
                              window.dispatchEvent(new PopStateEvent("popstate"));
                            }
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200/50 text-xs font-bold text-gray-700 hover:text-indigo-700 hover:bg-indigo-50/30 cursor-pointer text-left"
                        >
                          <LifeBuoy className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                          <span>Support Center</span>
                        </button>

                        {actualIsAdmin && (
                          <button
                            onClick={() => {
                              setIsMobileMenuOpen(false);
                              if (navigate) {
                                navigate("/admin");
                              } else {
                                window.history.pushState(null, "", "/admin");
                                window.dispatchEvent(new PopStateEvent("popstate"));
                              }
                            }}
                            className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 rounded-lg border border-emerald-200 text-xs font-bold text-emerald-700 hover:bg-emerald-50 cursor-pointer text-left sm:col-span-2"
                          >
                            <Shield className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span>Open Admin Console</span>
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            onDashboardClick?.("delete-account");
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-red-50/40 dark:bg-red-950/20 rounded-lg border border-red-100 text-xs font-bold text-red-600 hover:bg-red-50 cursor-pointer text-left sm:col-span-2"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500 shrink-0" />
                          <span>Delete Account</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      onSignInClick();
                    }}
                    className="w-full py-3 bg-gray-50 hover:bg-gray-100 text-gray-800 border border-gray-200 rounded-xl font-bold text-sm flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <LogIn className="w-4 h-4 text-gray-500" />
                    Sign In to Account
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dynamic All-Device Friendly Search Overlay Modal */}
      <AnimatePresence>
        {isSearchOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-gray-950/60 backdrop-blur-sm z-50 flex items-start justify-center p-3 sm:p-6 md:p-10 overflow-y-auto"
            onClick={() => setIsSearchOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="max-w-3xl w-full bg-white dark:bg-slate-50 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] mt-4 sm:mt-10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Search Bar Header */}
              <div className="relative flex items-center justify-between border-b border-gray-100 dark:border-slate-100 p-4 sm:p-5 bg-gray-50/50 dark:bg-slate-100/10">
                <div className="flex items-center gap-3 flex-1">
                  <Search className="w-5 h-5 text-gray-400 shrink-0" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tools, calculators, blog articles..."
                    className="w-full bg-transparent border-0 outline-none text-sm sm:text-base font-bold text-gray-800 placeholder-gray-400/80 focus:ring-0"
                  />
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="px-2 py-1 text-[10px] sm:text-xs font-bold text-gray-500 hover:text-gray-700 bg-gray-200/50 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => setIsSearchOpen(false)}
                    className="p-1.5 sm:p-2 hover:bg-gray-200/60 text-gray-500 hover:text-gray-800 rounded-xl transition-all cursor-pointer"
                    title="Close Search (Esc)"
                  >
                    <X className="w-4 h-4 sm:w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Scrollable Results Panel */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 text-left max-h-[60vh]">
                {!searchQuery ? (
                  /* Initial Empty State / Quick Recommendations */
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-[11px] font-black uppercase text-gray-650 tracking-wider mb-3">
                        Suggested Quick Searches
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { text: "Plant Identifier", query: "plant identifier" },
                          { text: "Weed Identifier", query: "weed" },
                          { text: "Plant Disease", query: "disease" },
                          { text: "Watering Calculator", query: "watering" },
                          { text: "Soil pH Analyzer", query: "soil" },
                          { text: "Tree Identifier", query: "tree" },
                          { text: "Flower Identifier", query: "flower" },
                        ].map((item) => (
                          <button
                            key={item.text}
                            onClick={() => setSearchQuery(item.query)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-50 hover:bg-green-50 hover:text-green-700 border border-gray-200/60 hover:border-green-200 text-xs font-bold text-gray-600 transition-all cursor-pointer"
                          >
                            <Search className="w-3 h-3 text-gray-400 group-hover:text-green-600" />
                            {item.text}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 select-none">
                      <div className="space-y-1">
                        <h5 className="text-xs font-black text-gray-900">
                          Botanical Intelligence Network
                        </h5>
                        <p className="text-[10px] text-gray-450 font-semibold leading-relaxed">
                          Enter terms like scientific names, pests, symptoms, or tool names to locate dynamic calculators or master blog posts.
                        </p>
                      </div>
                      <span className="text-[10px] font-bold text-gray-450 bg-gray-100 border border-gray-200/50 px-2.5 py-1 rounded-lg">
                        Press ESC to exit
                      </span>
                    </div>
                  </div>
                ) : (
                  /* Active Search Results */
                  <div className="space-y-6">
                    {/* Summary Count Badge */}
                    <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                      <span className="text-xs text-gray-550 font-bold">
                        Search results for <span className="text-gray-900 font-extrabold font-mono">"{searchQuery}"</span>
                      </span>
                      <span className="text-[10px] font-black uppercase tracking-wider bg-green-50 border border-green-150 text-green-700 px-2 py-0.5 rounded-md">
                        {searchResults.tools.length + searchResults.blogs.length} matches found
                      </span>
                    </div>

                    {searchResults.tools.length === 0 && searchResults.blogs.length === 0 ? (
                      /* No Results State */
                      <div className="py-10 text-center space-y-3">
                        <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-400 mx-auto border border-gray-100">
                          <Leaf className="w-6 h-6 text-gray-300" />
                        </div>
                        <div>
                          <h4 className="text-sm font-black text-gray-900">
                            No Botanical Matches Found
                          </h4>
                          <p className="text-xs text-gray-500 font-semibold max-w-sm mx-auto mt-1 leading-normal">
                            We couldn't find any tools or blog articles matching "{searchQuery}". Try searching for 'watering', 'disease', or 'weed'.
                          </p>
                        </div>
                      </div>
                    ) : (
                      /* Matching List */
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                        {/* Left Column: Tools & Interactive Calculators */}
                        {searchResults.tools.length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-800 rounded-xl border border-emerald-100/50 self-start w-fit">
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                              <span className="text-[10px] font-black uppercase tracking-wider">
                                Matching Tools ({searchResults.tools.length})
                              </span>
                            </div>

                            <div className="space-y-2">
                              {searchResults.tools.map((tool) => {
                                const ToolIcon = getToolIcon(tool.icon);
                                return (
                                  <button
                                    key={tool.id}
                                    onClick={() => {
                                      setIsSearchOpen(false);
                                      if (tool.link.startsWith("/")) {
                                        if (navigate) navigate(tool.link);
                                      } else {
                                        window.location.href = tool.link;
                                      }
                                    }}
                                    className="w-full flex items-start gap-3.5 p-3 rounded-2xl hover:bg-emerald-50/25 border border-gray-100 hover:border-emerald-100/40 transition-all text-left cursor-pointer group"
                                  >
                                    <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl group-hover:bg-emerald-100 transition-colors shrink-0">
                                      <ToolIcon className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center justify-between">
                                        <span className="block text-xs font-extrabold text-gray-900 group-hover:text-emerald-800 transition-all">
                                          {tool.name}
                                        </span>
                                        <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all shrink-0 ml-1" />
                                      </div>
                                      <span className="block text-[10px] text-gray-500 mt-1 leading-normal font-medium">
                                        {tool.description}
                                      </span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Right Column: Blog & Educational Articles */}
                        {searchResults.blogs.length > 0 && (
                          <div className={`space-y-3 ${searchResults.tools.length > 0 ? "border-t md:border-t-0 md:border-l border-gray-100 pt-5 md:pt-0 md:pl-6" : ""}`}>
                            <div className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-800 rounded-xl border border-green-100/50 self-start w-fit">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                              <span className="text-[10px] font-black uppercase tracking-wider">
                                Blog Articles ({searchResults.blogs.length})
                              </span>
                            </div>

                            <div className="space-y-2">
                              {searchResults.blogs.map((blog) => (
                                <button
                                  key={blog.slug}
                                  onClick={() => {
                                    setIsSearchOpen(false);
                                    if (navigate) {
                                      navigate(`/blog/${blog.slug}`);
                                    } else {
                                      window.location.href = `/blog/${blog.slug}`;
                                    }
                                  }}
                                  className="w-full flex items-start gap-3.5 p-3 rounded-2xl hover:bg-green-50/20 border border-gray-100 hover:border-green-150/45 transition-all text-left cursor-pointer group"
                                >
                                  {blog.image && (
                                    <img
                                      src={blog.image}
                                      alt=""
                                      className="w-12 h-12 rounded-xl object-cover border border-gray-100 group-hover:scale-105 transition-transform shrink-0"
                                      referrerPolicy="no-referrer"
                                    />
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between">
                                      <span className="block text-[10px] font-black text-green-600 uppercase tracking-widest">
                                        {blog.category}
                                      </span>
                                      <span className="text-[9px] font-semibold text-gray-400 font-mono shrink-0">
                                        {blog.reading_time}
                                      </span>
                                    </div>
                                    <span className="block text-xs font-extrabold text-gray-900 group-hover:text-green-800 mt-0.5 leading-snug line-clamp-2 transition-all">
                                      {blog.title}
                                    </span>
                                    <span className="block text-[9.5px] text-gray-450 mt-1 line-clamp-1 leading-normal font-semibold">
                                      {blog.summary}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  );
}
